/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: constants/routes.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Central route constants for the Ring2All Platform frontend.
    This file contains all route paths to avoid typos and make
    refactoring easier.

 🔧 Notes:
    - All route paths should be defined here
    - Use these constants instead of hardcoded strings
    - Grouped by functional area
────────────────────────────────────────────────────────────────*/

export const ROUTES = {
   // Public routes
   LOGIN: '/login',
   UNAUTHORIZED: '/unauthorized',
   ROOT: '/',
   DASHBOARD: '/dashboard',

   // User Management
   USERS: '/users',
   USER_DETAIL: (id: string | number) => `/users/${id}`,
   ROLE_PROFILES: '/role-profiles',
   ROLE_PROFILE_DETAIL: (id: string | number) => `/role-profiles/${id}`,
   LOG_PROFILES: '/log-profiles',
   LOG_PROFILE_DETAIL: (id: string | number) => `/log-profiles/${id}`,
   USER_PROFILE: '/user-profile',

   // System Configuration
   API_KEYS: '/api-keys',
   API_KEY_DETAIL: (id: string | number) => `/api-keys/${id}`,
   SYSTEM_MISC: '/settings/system/misc',
   SYSTEM_MISC_DETAIL: (id: string | number) => `/settings/system/misc/${id}`,
   EMAIL_SETTINGS: '/settings/system/email',
   EMAIL_SETTINGS_DETAIL: (id: string | number) => `/settings/system/email/${id}`,
   LOGGING_SETTINGS: '/settings/system/logging',
   LOGGING_SETTINGS_DETAIL: (id: string | number) => `/settings/system/logging/${id}`,
   NETWORK_SETTINGS: '/admin/network/settings',
   NETWORK_SETTING_DETAIL: (id: string | number) => `/admin/network/settings/${id}`,
   APPEARANCE: '/settings/appearance',

   // Telephony Infrastructure
   TELEPHONY_SERVERS: '/settings/technology/servers',
   TELEPHONY_SERVER_DETAIL: (id: string | number) => `/settings/technology/servers/${id}`,
   TELEPHONY_DOMAINS: '/settings/technology/domains',
   TELEPHONY_DOMAIN_DETAIL: (id: string | number) => `/settings/technology/domains/${id}`,
   TENANTS: '/settings/tenants',
   TENANT_DETAIL: (id: string | number) => `/settings/tenants/${id}`,

   // SIP Configuration
   SIP_PROFILES: '/settings/technology/sip',
   SIP_PROFILE_DETAIL: (id: string | number) => `/settings/technology/sip/${id}`,
   GATEWAY_SIP_PROFILES: '/settings/technology/gateway-sip-profiles',
   GATEWAY_SIP_PROFILE_DETAIL: (id: string | number) => `/settings/technology/gateway-sip-profiles/${id}`,
   DEVICE_SIP_PROFILES: '/settings/technology/device-sip-profiles',
   DEVICE_SIP_PROFILE_DETAIL: (id: string | number) => `/settings/technology/device-sip-profiles/${id}`,

   // Feature Codes
   FEATURE_CODES: '/settings/feature-codes',
   FEATURE_CATEGORIES: '/settings/feature-categories',
   FEATURE_CATEGORY_DETAIL: (id: string | number) => `/settings/feature-categories/${id}`,

   // Gateways
   GATEWAYS: '/settings/gateways',
   GATEWAY_DETAIL: (id: string | number) => `/settings/gateways/${id}`,

   // Extensions
   EXTENSIONS: '/pbx/extensions',
   EXTENSION_DETAIL: (id: string | number) => `/pbx/extensions/${id}`,

   // IVR
   IVRS: '/pbx/ivrs',
   IVR_DETAIL: (id: string | number) => `/pbx/ivrs/${id}`,

   // Ring Groups
   RING_GROUPS: '/pbx/ring-groups',
   RING_GROUP_DETAIL: (id: string | number) => `/pbx/ring-groups/${id}`,

   // Call Flows
   CALL_FLOWS: '/pbx/call-flows',
   CALL_FLOW_DETAIL: (id: string | number) => `/pbx/call-flows/${id}`,

   // Conferences
   CONFERENCES: '/pbx/conferences',
   CONFERENCE_DETAIL: (id: string | number) => `/pbx/conferences/${id}`,

   // Voicemail
   VOICEMAILS: '/pbx/voicemails',
   VOICEMAIL_DETAIL: (id: string | number) => `/pbx/voicemails/${id}`,
   VOICEMAIL_SETTINGS: '/pbx/voicemail-settings',
   VOICEMAIL_PROFILES: '/pbx/voicemail-profiles',
   VOICEMAIL_PROFILE_DETAIL: (id: string | number) => `/pbx/voicemail-profiles/${id}`,
   VOICEMAIL_BROADCAST_GROUPS: '/pbx/voicemail-broadcast-groups',
   VOICEMAIL_BROADCAST_GROUP_DETAIL: (id: string | number) => `/pbx/voicemail-broadcast-groups/${id}`,

   // Call Center
   QUEUES: '/call-center/queues',
   QUEUE_DETAIL: (id: string | number) => `/call-center/queues/${id}`,
   QUEUE_PROFILES: '/call-center/queue-profiles',
   QUEUE_PROFILE_DETAIL: (id: string | number) => `/call-center/queue-profiles/${id}`,
   AGENTS: '/call-center/agents',
   AGENT_DETAIL: (id: string | number) => `/call-center/agents/${id}`,
   CALLBACK_PROFILES: '/call-center/callback-profiles',
   CALLBACK_PROFILE_DETAIL: (id: string | number) => `/call-center/callback-profiles/${id}`,
   CALLBACK_RULES: '/call-center/callback-rules',
   CALLBACK_RULE_DETAIL: (id: string | number) => `/call-center/callback-rules/${id}`,

   // Advanced Features
   TIME_CONDITIONS: '/pbx/time-conditions',
   TIME_CONDITION_DETAIL: (id: string | number) => `/pbx/time-conditions/${id}`,
   TIME_GROUPS: '/pbx/time-groups',
   TIME_GROUP_DETAIL: (id: string | number) => `/pbx/time-groups/${id}`,
   DISA_ENTRIES: '/pbx/disa-entries',
   DISA_ENTRY_DETAIL: (id: string | number) => `/pbx/disa-entries/${id}`,
   DIRECT_DIAL: '/pbx/direct-dial',
   DIRECT_DIAL_DETAIL: (id: string | number) => `/pbx/direct-dial/${id}`,
   DIRECT_ROUTE: '/pbx/direct-route',
   DIRECT_ROUTE_DETAIL: (id: string | number) => `/pbx/direct-route/${id}`,
   HOT_DESKING: '/pbx/hot-desking',
   HOT_DESKING_DETAIL: (id: string | number) => `/pbx/hot-desking/${id}`,
   PICKUP_GROUPS: '/pbx/pickup-groups',
   PICKUP_GROUP_DETAIL: (id: string | number) => `/pbx/pickup-groups/${id}`,
   PAGING_GROUPS: '/pbx/paging-groups',
   PAGING_GROUP_DETAIL: (id: string | number) => `/pbx/paging-groups/${id}`,
   PARKING_LOTS: '/pbx/parking-lots',
   PARKING_LOT_DETAIL: (id: string | number) => `/pbx/parking-lots/${id}`,
   SPEED_DIALS: '/pbx/speed-dials',
   SPEED_DIAL_DETAIL: (id: string | number) => `/pbx/speed-dials/${id}`,

   // Security & Restrictions
   BLACKLIST: '/pbx/blacklist',
   BLACKLIST_DETAIL: (id: string | number) => `/pbx/blacklist/${id}`,
   PIN_LISTS: '/pbx/calls-routing/pin-lists',
   PIN_LIST_DETAIL: (id: string | number) => `/pbx/calls-routing/pin-lists/${id}`,
   AUTHORIZATION_CODES: '/pbx/authorization-codes',
   AUTHORIZATION_CODE_DETAIL: (id: string | number) => `/pbx/authorization-codes/${id}`,
   AUTHENTICATION_CODES: '/pbx/authentication-codes',
   AUTHENTICATION_CODE_DETAIL: (id: string | number) => `/pbx/authentication-codes/${id}`,
   DIAL_RULE_RESTRICTIONS: '/pbx/dial-rule-restrictions',
   DIAL_RULE_RESTRICTION_DETAIL: (id: string | number) => `/pbx/dial-rule-restrictions/${id}`,
   CLASS_OF_SERVICES: '/pbx/class-of-services',
   CLASS_OF_SERVICE_DETAIL: (id: string | number) => `/pbx/class-of-services/${id}`,

   // Advanced Routing
   INBOUND_ROUTES: '/pbx/inbound-routes',
   INBOUND_ROUTE_DETAIL: (id: string | number) => `/pbx/inbound-routes/${id}`,
   OUTBOUND_ROUTES: '/pbx/outbound-routes',
   OUTBOUND_ROUTE_DETAIL: (id: string | number) => `/pbx/outbound-routes/${id}`,
   DYNAMIC_DESTINATIONS: '/pbx/dynamic-destinations',
   DYNAMIC_DESTINATION_DETAIL: (id: string | number) => `/pbx/dynamic-destinations/${id}`,
   ARS: '/pbx/ars',
   ARS_DETAIL: (id: string | number) => `/pbx/ars/${id}`,
   CID_MODIFIERS: '/pbx/cid-modifiers',
   CID_MODIFIER_DETAIL: (id: string | number) => `/pbx/cid-modifiers/${id}`,
   CALLER_ID_LOOKUP_SOURCES: '/pbx/caller-id-lookup-sources',
   CALLER_ID_LOOKUP_SOURCE_DETAIL: (id: string | number) => `/pbx/caller-id-lookup-sources/${id}`,

   // Media & Announcements
   MOH_SOURCES: '/pbx/moh-sources',
   MOH_SOURCE_DETAIL: (id: string | number) => `/pbx/moh-sources/${id}`,
   ANNOUNCEMENTS: '/pbx/announcements',
   ANNOUNCEMENT_DETAIL: (id: string | number) => `/pbx/announcements/${id}`,
   VOICE_GUIDES: '/pbx/voice-guides',
   VOICE_GUIDE_DETAIL: (id: string | number) => `/pbx/voice-guides/${id}`,

   // Recording
   RECORDING_MANAGEMENT: '/pbx/recording-management',
   RECORDING_MANAGEMENT_DETAIL: (id: string | number) => `/pbx/recording-management/${id}`,

   // Advanced Features
   CUSTOMER_CODES: '/pbx/customer-codes',
   CUSTOMER_CODE_DETAIL: (id: string | number) => `/pbx/customer-codes/${id}`,
   ESL_USERS: '/pbx/esl-users',
   ESL_USER_DETAIL: (id: string | number) => `/pbx/esl-users/${id}`,
   VIP_LISTS: '/pbx/vip-lists',
   VIP_LIST_DETAIL: (id: string | number) => `/pbx/vip-lists/${id}`,
   CONFERENCE_PROFILES: '/pbx/conference-profiles',
   CONFERENCE_PROFILE_DETAIL: (id: string | number) => `/pbx/conference-profiles/${id}`,
   DIAL_PROFILES: '/pbx/dial-profiles',
   DIAL_PROFILE_DETAIL: (id: string | number) => `/pbx/dial-profiles/${id}`,

   // Branding
   BRANDING: '/settings/branding',
   BRANDING_DETAIL: (id: string | number) => `/settings/branding/${id}`,

   // Form Builder
   FORM_BUILDER: '/form-builder',
   FORM_BUILDER_DETAIL: (id: string | number) => `/form-builder/${id}`,

   // AI
   AI_PROVIDERS: '/settings/ai/providers',
   AI_PROVIDER_DETAIL: (id: string | number) => `/settings/ai/providers/${id}`,
   AI_PROFILES: '/settings/ai/profiles',
   AI_PROFILE_DETAIL: (id: string | number) => `/settings/ai/profiles/${id}`,

   // PBX CLI
   PBX_CLI: '/pbx-cli',

   // CDR & Reports
   CDR_SETTINGS: '/reports/cdr-settings',
   INTEGRATIONS: '/settings/integrations',
   INTEGRATIONS_LIST: '/settings/integrations/list',
   INTEGRATION_CONFIGURATION: '/settings/integrations/:provider',
   INTEGRATION_NEW: '/settings/integrations/:provider/new',
   INTEGRATION_EDIT: '/settings/integrations/:provider/:connectionId',
   CDR_FILTERS: '/reports/cdr/filters',
   CDR_FILTER_DETAIL: (id: string | number) => `/reports/cdr/filters/${id}`,
   CALL_DETAIL_RECORDS: '/reports/cdr/cdr',
   BULK_MODIFICATION: '/pbx/bulk-modification',

   // Languages
   LANGUAGES: '/settings/languages',
   LANGUAGE_DETAIL: (id: string | number) => `/settings/languages/${id}`,

   // Maintenance
   BACKUP_RESTORE: '/admin/maintenance/backup',
   SYSTEM_CLEANUP: '/admin/maintenance/system',

   // System General
   SYSTEM_GENERAL: '/settings/system/general',

   // SMS Messaging
   SMS_PROVIDERS: '/pbx/sms/providers',
   SMS_PROVIDER_DETAIL: (id: string | number) => `/pbx/sms/providers/${id}`,
   SMS_NUMBERS: '/pbx/sms/numbers',
   SMS_NUMBERS_NEW: '/pbx/sms/numbers/new',
   SMS_NUMBER_DETAIL: (id: string | number) => `/pbx/sms/numbers/${id}`,
   SMS_ROUTES: '/pbx/sms/routes',
   SMS_ROUTES_NEW: '/pbx/sms/routes/new',
   SMS_ROUTE_DETAIL: (id: string | number) => `/pbx/sms/routes/${id}`,
   SMS_MESSAGES: '/pbx/sms/messages',
   SMS_SETTINGS: '/pbx/sms/settings',
   SMS_OPTOUTS: '/pbx/sms/optouts',
} as const;

